'use client';

import React, { useRef } from 'react';
import { Card, CardContent } from '@/atoms/Card/Card';
import { Container } from '@/atoms/Container/Container';
import { PostThreadConnector } from '@/atoms/PostThreadConnector/PostThreadConnector';
import { POST_THREAD_CONNECTOR_VARIANTS } from '@/atoms/PostThreadConnector/PostThreadConnector.constants';
import { TIMELINE_FEED_VARIANT } from '@/config/feed';
import { useEffectiveTagsLayout } from '@/hooks/useEffectiveTagsLayout/useEffectiveTagsLayout';
import { useElementHeight } from '@/hooks/useElementHeight/useElementHeight';
import { useIsMobile } from '@/hooks/useIsMobile/useIsMobile';
import { usePostDetails } from '@/hooks/usePostDetails/usePostDetails';
import { usePostHeaderVisibility } from '@/hooks/usePostHeaderVisibility/usePostHeaderVisibility';
import {
  getDisplayedPostId,
  getInteractionPostId,
} from '@/hooks/usePostHeaderVisibility/usePostHeaderVisibility.utils';
import { usePostNavigation } from '@/hooks/usePostNavigation/usePostNavigation';
import { usePostReplyRepostDialogs } from '@/hooks/usePostReplyRepostDialogs/usePostReplyRepostDialogs';
import { useRelativeTime } from '@/hooks/useRelativeTime/useRelativeTime';
import { useRemoveDeletedPost } from '@/hooks/useRemoveDeletedPost/useRemoveDeletedPost';
import { useTtlSubscription } from '@/hooks/useTtlSubscription/useTtlSubscription';
import { useUndoRepost } from '@/hooks/useUndoRepost/useUndoRepost';
import { cn, isPostDeleted } from '@/libs/utils/utils';
import { PostPreviewCard } from '@/molecules/PostPreviewCard/PostPreviewCard';
import { PostUnavailable } from '@/molecules/PostUnavailable/PostUnavailable';
import { RepostHeader } from '@/molecules/RepostHeader/RepostHeader';
import { PostSavePicker } from '@/organisms/PostSavePicker/PostSavePicker';
import { useTimelineFeedContext } from '@/organisms/Timeline/Feed/TimelineFeed/TimelineFeedContext';
import { PostActionsBar } from '../PostActionsBar/PostActionsBar';
import { PostContent } from '../PostContent/PostContent';
import { PostHeader } from '../PostHeader/PostHeader';
import { PostInlineTagsActions } from '../PostInlineTagsActions/PostInlineTagsActions';
import { PostTagsPanel } from '../PostTagsPanel/PostTagsPanel';
import type { PostTagsPanelHandle } from '../PostTagsPanel/PostTagsPanel.types';
import type { PostMainProps } from './PostMain.types';
import { PostMainListRow } from './PostMainListRow/PostMainListRow';
import { WIDE_POST_BODY_TEXT_CLASS } from './PostMainTypography';

// Stops click and middle-click from bubbling to the outer post-card navigation
// (so action buttons and tag panels don't trigger a navigate / new-tab open).
const stopCardPropagation = (event: React.MouseEvent) => event.stopPropagation();

function RemovablePostUnavailable({
  postId,
  message,
  removeDataCy,
}: {
  postId: string;
  message: string;
  removeDataCy: string;
}) {
  const removal = useRemoveDeletedPost(postId);

  return (
    <PostUnavailable
      message={message}
      removeDataCy={removeDataCy}
      onRemove={removal.canRemove ? () => void removal.remove() : undefined}
      isRemoving={removal.isRemoving}
    />
  );
}

// Wires the contentless-repost header: deletes the repost itself on Undo and
// formats the share time. Mounted only when `showRepostHeader` is true so the
// extra hooks don't run for every card in the feed.
function UndoableRepostHeader({
  postId,
  isCollectionShare,
  indexedAt,
  hasBodyActions,
}: {
  postId: string;
  isCollectionShare: boolean;
  indexedAt: Date | null;
  hasBodyActions: boolean;
}) {
  const { formatRelativeTime } = useRelativeTime();
  const { undoRepost, isUndoing } = useUndoRepost(isCollectionShare);
  const feed = useTimelineFeedContext();
  const isLibraryFeed =
    feed?.variant === TIMELINE_FEED_VARIANT.BOOKMARKS || feed?.variant === TIMELINE_FEED_VARIANT.COLLECTION;

  return (
    <RepostHeader
      isCollectionShare={isCollectionShare}
      onUndo={() => void undoRepost(postId)}
      isUndoing={isUndoing}
      timeAgo={indexedAt ? formatRelativeTime(indexedAt) : null}
      indexedAt={indexedAt}
    >
      {!hasBodyActions && isLibraryFeed && (
        <Container overrideDefaults onClick={stopCardPropagation} onAuxClick={stopCardPropagation}>
          <PostSavePicker postId={postId} buttonClassName="border-none shadow-xs" />
        </Container>
      )}
    </RepostHeader>
  );
}

export function PostMain({
  postId,
  postDetails: providedPostDetails,
  className,
  isReply = false,
  isLastReply = false,
  pinActionsToBottom = false,
  isNavigable = true,
  showFullContentInListLayout = false,
  presentation = 'default',
}: PostMainProps) {
  const isCards = presentation === 'cards';
  const effectiveTagsLayout = useEffectiveTagsLayout();
  const isWideLayout = effectiveTagsLayout === 'side';
  const isListLayout = effectiveTagsLayout === 'list';
  const detailsQuery = usePostDetails(postId, { enabled: providedPostDetails === undefined });
  const postDetails = providedPostDetails === undefined ? detailsQuery.postDetails : providedPostDetails;
  const isLoading = providedPostDetails === undefined && detailsQuery.isLoading;
  const isDeleted = isPostDeleted(postDetails);
  // A settled `null` (cache miss after the fetch resolved) means the post 404'd.
  // Without this branch the card below would skeleton forever (PostHeader /
  // PostContent each wait on `postDetails`). `undefined` is still loading.
  const isMissing = postDetails === null && !isLoading;

  const { handlePostClick, handlePostAuxClick } = usePostNavigation();

  const headerVisibility = usePostHeaderVisibility(postDetails ? postId : '');
  const { showRepostHeader, shouldShowPostHeader, originalPostId } = headerVisibility;
  // A contentless repost adds a bar above the original post, not another card
  // around it. Undo and saved membership retain the repost's identity.
  const repostedPostId = showRepostHeader ? originalPostId : null;
  const displayedPostId = getDisplayedPostId(postId, headerVisibility);
  const showDisplayedPostHeader = repostedPostId !== null || shouldShowPostHeader;
  // Tags and replies of a contentless repost go to the original for every viewer; tags already
  // stored on the repost id are merged into the panel so they stay visible.
  const interactionPostId = getInteractionPostId(postId, headerVisibility);
  const tagsMergePostId = interactionPostId !== postId ? postId : undefined;
  // Contentless collection shares get a distinct full-bleed treatment (#2121):
  // repost header + flush CollectionCard, no post chrome (header, actions, tags).
  // The original's details query stays disabled unless the header is showing.
  const { postDetails: originalPostDetails, isLoading: isOriginalLoading } = usePostDetails(repostedPostId);
  const isOriginalMissing = repostedPostId !== null && originalPostDetails === null && !isOriginalLoading;
  const isOriginalDeleted = repostedPostId !== null && isPostDeleted(originalPostDetails);
  const isCollectionShare = repostedPostId !== null && originalPostDetails?.kind === 'collection';
  const { openReplyDialog, openRepostDialog, dialogs } = usePostReplyRepostDialogs(interactionPostId);

  const mobileTagsPanelRef = useRef<PostTagsPanelHandle>(null);
  const desktopTagsPanelRef = useRef<PostTagsPanelHandle>(null);
  const isMobile = useIsMobile();

  // Get post height for thread connector
  const { ref: cardRef, height: postHeight } = useElementHeight();

  // Subscribe to TTL coordinator based on viewport visibility
  const { ref: ttlRef } = useTtlSubscription({
    type: 'post',
    id: postId,
  });
  // PostPreviewCard owns collection freshness; flattened ordinary reposts must
  // keep refreshing the original even though its preview wrapper is gone.
  const { ref: originalTtlRef } = useTtlSubscription({
    type: 'post',
    id: isCollectionShare && !isOriginalDeleted ? null : repostedPostId,
  });

  // Determine thread connector variant based on reply status
  const connectorVariant = isLastReply ? POST_THREAD_CONNECTOR_VARIANTS.LAST : POST_THREAD_CONNECTOR_VARIANTS.REGULAR;

  return (
    <>
      <Container
        ref={(node) => {
          ttlRef(node);
          originalTtlRef(node);
        }}
        overrideDefaults
        onClick={isNavigable ? (e) => handlePostClick(displayedPostId, e) : undefined}
        onAuxClick={isNavigable ? (e) => handlePostAuxClick(displayedPostId, e) : undefined}
        className={cn(
          'relative flex min-w-0',
          !isCards && '@max-xl/grid:h-full',
          isNavigable && 'cursor-pointer',
          isReply && 'pl-3',
        )}
      >
        {isReply && (
          <Container overrideDefaults className="absolute top-0 bottom-0 left-0 w-3">
            <PostThreadConnector height={postHeight} variant={connectorVariant} />
          </Container>
        )}
        <Card ref={cardRef} className={cn('min-w-0 flex-1 gap-0 rounded-md py-0', className)}>
          {isMissing ? (
            <RemovablePostUnavailable
              postId={postId}
              message={'Post not found.'}
              removeDataCy="post-missing-remove-btn"
            />
          ) : isDeleted ? (
            <RemovablePostUnavailable
              postId={postId}
              message={'This post has been deleted by its author.'}
              removeDataCy="post-deleted-remove-btn"
            />
          ) : (
            <>
              {showRepostHeader && (
                <UndoableRepostHeader
                  postId={postId}
                  hasBodyActions={!isCollectionShare && !isOriginalMissing && !isOriginalDeleted}
                  isCollectionShare={isCollectionShare}
                  indexedAt={postDetails ? new Date(postDetails.indexed_at) : null}
                />
              )}
              {isOriginalMissing ? (
                <PostUnavailable message={'Post not found.'} />
              ) : isOriginalDeleted ? (
                <PostUnavailable message={'This post has been deleted by its author.'} />
              ) : isCollectionShare ? (
                // Full-bleed collection share: the CollectionCard is the whole
                // body — flush under the header, no padding, no actions/tags.
                <CardContent className="flex min-w-0 flex-col p-0 @max-xl/grid:flex-1">
                  <PostPreviewCard postId={displayedPostId} flush />
                </CardContent>
              ) : (
                <CardContent
                  className={cn(
                    'flex min-w-0 flex-col',
                    !isCards && '@max-xl/grid:flex-1',
                    isWideLayout || isListLayout ? 'p-0' : isCards ? 'gap-6 p-6' : 'gap-4 p-6',
                  )}
                >
                  {isListLayout ? (
                    <PostMainListRow
                      postId={displayedPostId}
                      savePostId={postId}
                      showFullContent={!isReply && showFullContentInListLayout}
                      shouldShowPostHeader={showDisplayedPostHeader}
                      onReplyClick={openReplyDialog}
                      onRepostClick={openRepostDialog}
                    />
                  ) : isWideLayout ? (
                    <Container className="flex min-w-0 flex-col lg:flex-row">
                      <Container className="flex min-w-0 flex-col gap-4 p-12 lg:flex-1">
                        {showDisplayedPostHeader && (
                          <PostHeader postId={displayedPostId} size="extraLarge" timeAgoPlacement="bottom-left" />
                        )}
                        <PostContent postId={displayedPostId} textClassName={WIDE_POST_BODY_TEXT_CLASS} />
                        {pinActionsToBottom && <Container overrideDefaults className="flex-1" />}
                        <Container
                          overrideDefaults
                          onClick={stopCardPropagation}
                          onAuxClick={stopCardPropagation}
                          className="flex flex-col gap-4"
                        >
                          <PostTagsPanel
                            ref={mobileTagsPanelRef}
                            postId={interactionPostId}
                            mergePostId={tagsMergePostId}
                            widthMode="full"
                            className="lg:hidden"
                          />
                          <PostActionsBar
                            postId={interactionPostId}
                            savePostId={postId}
                            onTagClick={() => {
                              // The tag button only reveals the tags. On mobile that reveal must not
                              // focus the input and pop the soft keyboard: the `[+]` add control
                              // owns autofocus. It still has to bring the panel into view, which the
                              // desktop path gets from `focus()` as a side effect.
                              if (isMobile) {
                                mobileTagsPanelRef.current?.reveal();
                                return;
                              }
                              mobileTagsPanelRef.current?.focus();
                              desktopTagsPanelRef.current?.focus();
                            }}
                            onReplyClick={openReplyDialog}
                            onRepostClick={openRepostDialog}
                          />
                        </Container>
                      </Container>
                      <Container
                        overrideDefaults
                        onClick={stopCardPropagation}
                        onAuxClick={stopCardPropagation}
                        className="hidden lg:flex lg:w-96 lg:shrink-0 lg:p-12"
                      >
                        <PostTagsPanel
                          ref={desktopTagsPanelRef}
                          postId={interactionPostId}
                          mergePostId={tagsMergePostId}
                          widthMode="full"
                          className="w-full"
                        />
                      </Container>
                    </Container>
                  ) : (
                    <>
                      {showDisplayedPostHeader && <PostHeader postId={displayedPostId} />}
                      <PostContent postId={displayedPostId} mediaVariant={isCards ? 'cards' : 'default'} />
                      <PostInlineTagsActions
                        presentation={presentation}
                        postId={interactionPostId}
                        tagsMergePostId={tagsMergePostId}
                        savePostId={postId}
                        onReplyClick={openReplyDialog}
                        onRepostClick={openRepostDialog}
                        actionsClassName="w-full shrink-0 justify-start sm:w-auto md:justify-end @max-xl/grid:w-full! @max-xl/grid:justify-start!"
                      />
                    </>
                  )}
                </CardContent>
              )}
            </>
          )}
        </Card>
      </Container>
      {dialogs}
    </>
  );
}
